import React from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from 'react-admin';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import { useTagNameSource } from '@/i18n/hooks.js';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';

const Element = ({ tag, total }) => {
    const nameSource = useTagNameSource();
    const percentage = total > 0 ? (tag.current / total) * 100 : 0;

    return (
        <Link key={tag.id} to='/analytic-products' className='tag-link' style={{ textDecoration: 'none' }}>
            <Chip
                label={
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            width: '100%',
                        }}
                    >
                        <Typography variant='inherit' component='span'>
                            {tag[nameSource]}:
                        </Typography>
                        <Typography variant='inherit' component='span' sx={{ fontWeight: 600 }}>
                            {percentage.toFixed(1)}%
                        </Typography>
                    </Box>
                }
                sx={{
                    backgroundColor: tag.categoryColor || Palette.violet.violet100,
                    color: Palette.black,
                    height: '38px',
                    borderRadius: '12px',
                    padding: '0px 10px 0px 16px',
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        height: 'auto',
                        padding: '4px 12px',
                    },
                    '& .MuiChip-label': {
                        fontSize: '16px',
                        padding: '0px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                            fontSize: '11px',
                            lineHeight: '16px',
                        },
                    },
                    '&:hover': {
                        backgroundColor: tag.categoryColor || Palette.violet.violet100,
                    },
                }}
                clickable
            />
        </Link>
    );
};

Element.propTypes = {
    tag: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
        nameEn: PropTypes.string,
        current: PropTypes.number.isRequired,
        categoryName: PropTypes.string,
        categoryColor: PropTypes.string,
    }).isRequired,
    total: PropTypes.number.isRequired,
};

const DeviceDetailsTopTags = ({ topTags, isLoading, error, total }) => {
    const translate = useTranslate();
    const tags = (topTags?.tags || []).filter(tag => tag.current > 0);

    return (
        <Box className='padding-wrapper device-details-stats'>
            <Typography variant='h5' fontSize='22px'>
                {translate('resources.device.labels.topTags')}
            </Typography>
            <Box sx={{ marginTop: '24px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {isLoading ? (
                    <CircularProgress />
                ) : error ? (
                    <NotificationBox variant='error' className='large'>
                        {translate('resources.device.labels.loadingError')}
                    </NotificationBox>
                ) : (
                    tags.map(tag => <Element key={tag.id} tag={tag} total={total} />)
                )}
                {!isLoading && tags.length < 10 && (
                    <NotificationBox variant='info' className='large'>
                        {translate('resources.device.labels.noDataError')}
                    </NotificationBox>
                )}
            </Box>
        </Box>
    );
};

DeviceDetailsTopTags.propTypes = {
    topTags: PropTypes.shape({
        tags: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                name: PropTypes.string,
                nameEn: PropTypes.string,
                current: PropTypes.number,
                categoryName: PropTypes.string,
                categoryColor: PropTypes.string,
            }),
        ),
    }),
    isLoading: PropTypes.bool,
    error: PropTypes.object,
    total: PropTypes.number,
};

export default DeviceDetailsTopTags;
